import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Compact dropdown used in the Header (light) and Footer (dark) desktop layouts.
export const LanguageSwitcher = ({ tone = 'light' }) => {
  const { language, setLanguage, languages, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === language) || languages[0];
  const isDark = tone === 'dark';

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('header.selectLanguage')}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="language-switcher-btn"
        className={
          isDark
            ? 'flex items-center gap-1 rounded-full border border-white/25 px-2.5 py-2 transition-colors hover:border-white/50'
            : 'flex items-center gap-1 rounded-full border border-[#1A2E6E]/15 px-2.5 py-2 transition-colors hover:border-[#1A2E6E]/35 hover:bg-[#1A2E6E]/5'
        }
      >
        <img
          src={current.flag}
          alt={current.label}
          className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-black/10"
        />
        <ChevronDown size={14} className={`transition-transform duration-200 ${isDark ? 'text-white/90' : 'text-[#1A2E6E]'} ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            role="menu"
            className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-xl border border-[#E8EAF0] bg-white py-1.5 shadow-xl"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="menuitemradio"
                aria-checked={lang.code === language}
                onClick={() => {
                  setLanguage(lang.code);
                  setOpen(false);
                }}
                data-testid={`language-option-${lang.code}`}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#1A2E6E]/5 ${
                  lang.code === language ? 'font-semibold text-[#1A2E6E]' : 'text-[#1A2E6E]/80'
                }`}
              >
                <img src={lang.flag} alt="" className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-black/10" />
                <span className="flex-1">{lang.label}</span>
                {lang.code === language && <Check size={14} className="text-[#4CAF50]" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Inline list used in the Header's dark mobile menu panel.
export const LanguageSwitcherInline = () => {
  const { language, setLanguage, languages, t } = useLanguage();

  return (
    <div className="mt-8 border-t border-white/10 pt-6" data-testid="language-switcher-mobile">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-white/50">
        {t('header.selectLanguage')}
      </p>
      <div className="flex flex-col gap-2">
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            onClick={() => setLanguage(lang.code)}
            aria-pressed={lang.code === language}
            data-testid={`language-option-mobile-${lang.code}`}
            className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors ${
              lang.code === language
                ? 'bg-white/15 text-[#F5A623]'
                : 'text-white/80 hover:bg-white/5 hover:text-white'
            }`}
          >
            <img src={lang.flag} alt="" className="h-4 w-[22px] rounded-[2px] object-cover ring-1 ring-white/20" />
            <span className="flex-1">{lang.label}</span>
            {lang.code === language && <Check size={16} />}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
